<template>
  <b-modal
    :visible="visible"
    size="xl"
    :title="question ? 'Edit Question' : 'Create Question'"
    :ok-title="question ? 'Update' : 'Create'"
    :ok-disabled="isLoading"
    @ok="handleSubmit"
    @cancel="$emit('close')"
    @close="$emit('close')"
  >
    <div class="question-modal">
      <validation-observer v-slot="{ handleSubmit: validationHandleSubmit }" ref="formObserver">
        <b-form @submit.prevent="validationHandleSubmit(submitForm)">

          <!-- Question Type Selection -->
          <div class="mb-4">
            <h5 class="mb-3">
              <feather-icon icon="HelpCircleIcon" class="mr-1" size="18"/>
              Question Type
            </h5>
            <validation-provider
              v-slot="validationContext"
              name="Question Type"
            >
              <b-form-group>
                <div class="question-type-cards">
                  <div
                    v-for="type in questionTypes"
                    :key="type.value"
                    class="type-card"
                    :class="{ active: form.question_type === type.value }"
                    @click="form.question_type = type.value"
                  >
                    <feather-icon :icon="type.icon" size="24" class="mb-2"/>
                    <h6 class="mb-1">{{ type.label }}</h6>
                    <small class="text-muted">{{ type.description }}</small>
                  </div>
                </div>
                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </div>

          <!-- Question Content -->
          <div class="mb-4">
            <h5 class="mb-3">
              <feather-icon icon="EditIcon" class="mr-1" size="18"/>
              Question Content
            </h5>

            <b-row>
              <b-col md="8">
                <validation-provider
                  v-slot="validationContext"
                  name="Question"
                  rules="required|min:10"
                >
                  <b-form-group label="Question Text">
                    <b-form-textarea
                      v-model="form.question"
                      :state="getValidationState(validationContext)"
                      rows="4"
                      placeholder="Enter your question text..."
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col md="4">
                <b-form-group label="Points">
                  <b-form-input
                    v-model.number="form.points"
                    type="number"
                    min="0"
                    step="0.1"
                    placeholder="1"
                  />
                </b-form-group>

                <b-form-group label="Difficulty">
                  <v-select
                    v-model="form.difficulty_level"
                    :options="difficultyOptions"
                    placeholder="Medium"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Question Type Specific Fields -->
            <div v-if="form.question_type === 'multiple_choice'">
              <b-form-group label="Answer Options">
                <div v-for="(option, index) in form.options" :key="index" class="option-row mb-2">
                  <b-input-group>
                    <b-input-group-prepend>
                      <b-input-group-text>{{ String.fromCharCode(65 + index) }}</b-input-group-text>
                    </b-input-group-prepend>
                    <b-form-input
                      v-model="form.options[index]"
                      :placeholder="`Option ${String.fromCharCode(65 + index)}`"
                    />
                    <b-input-group-append>
                      <b-button
                        variant="outline-success"
                        :variant="form.correct_answer_index === index ? 'success' : 'outline-success'"
                        @click="setCorrectAnswer(index)"
                        size="sm"
                      >
                        <feather-icon icon="CheckIcon" size="14"/>
                      </b-button>
                      <b-button
                        variant="outline-danger"
                        @click="removeOption(index)"
                        size="sm"
                        :disabled="form.options.length <= 2"
                      >
                        <feather-icon icon="XIcon" size="14"/>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </div>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="addOption"
                  :disabled="form.options.length >= 6"
                >
                  <feather-icon icon="PlusIcon" size="14" class="mr-1"/>
                  Add Option
                </b-button>
              </b-form-group>
            </div>

            <div v-else-if="form.question_type === 'true_false'">
              <b-form-group label="Correct Answer">
                <b-form-radio-group v-model="form.correct_answer" :options="trueFalseOptions"/>
              </b-form-group>
            </div>

            <div v-else-if="form.question_type === 'essay'">
              <b-row>
                <b-col md="6">
                  <b-form-group label="Minimum Words">
                    <b-form-input
                      v-model.number="form.min_words"
                      type="number"
                      min="0"
                      placeholder="Optional"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Maximum Words">
                    <b-form-input
                      v-model.number="form.max_words"
                      type="number"
                      min="1"
                      placeholder="Optional"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-form-group label="Essay Prompt">
                <b-form-textarea
                  v-model="form.essay_prompt"
                  rows="3"
                  placeholder="Provide specific instructions for the essay..."
                />
              </b-form-group>
            </div>

            <div v-else-if="form.question_type === 'short_answer'">
              <b-form-group label="Expected Answer">
                <b-form-input
                  v-model="form.correct_answer"
                  placeholder="Enter the expected answer..."
                />
              </b-form-group>
            </div>

            <!-- Explanation -->
            <b-form-group label="Explanation (Optional)">
              <b-form-textarea
                v-model="form.explanation"
                rows="3"
                placeholder="Provide explanation for the correct answer..."
              />
            </b-form-group>
          </div>

        </b-form>
      </validation-observer>
    </div>

    <template #modal-footer>
      <b-button variant="outline-secondary" @click="$emit('close')">
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="isLoading"
        @click="handleSubmit"
      >
        <b-spinner v-if="isLoading" small class="mr-1"/>
        <feather-icon v-else icon="SaveIcon" size="16" class="mr-1"/>
        {{ question ? 'Update' : 'Create' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormRadioGroup,
  BFormInvalidFeedback,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupAppend,
  BInputGroupText,
  BRow,
  BCol,
  BButton,
  BSpinner
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import TestManagementApi from '@/api/testManagement'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'QuestionModal',
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormRadioGroup,
    BFormInvalidFeedback,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BInputGroupText,
    BRow,
    BCol,
    BButton,
    BSpinner,
    ValidationObserver,
    ValidationProvider,
    vSelect,
    ToastificationContent
  },

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    question: {
      type: Object,
      default: null
    },
    testPack: {
      type: Object,
      required: false,
      default: null
    }
  },

  data() {
    return {
      isLoading: false,
      form: {
        question_type: 'multiple_choice',
        question: '',
        explanation: '',
        points: 1,
        difficulty_level: 'medium',
        options: ['', '', '', ''],
        correct_answer: '',
        correct_answer_index: null,
        essay_prompt: '',
        min_words: null,
        max_words: null
      },

      questionTypes: [
        {
          value: 'multiple_choice',
          label: 'Multiple Choice',
          description: 'Students choose from multiple options',
          icon: 'CheckSquareIcon'
        },
        {
          value: 'true_false',
          label: 'True/False',
          description: 'Simple true or false questions',
          icon: 'ToggleLeftIcon'
        },
        {
          value: 'essay',
          label: 'Essay',
          description: 'Long-form written responses',
          icon: 'EditIcon'
        },
        {
          value: 'short_answer',
          label: 'Short Answer',
          description: 'Brief text responses',
          icon: 'TypeIcon'
        }
      ],

      difficultyOptions: [
        { label: 'Easy', value: 'easy' },
        { label: 'Medium', value: 'medium' },
        { label: 'Hard', value: 'hard' }
      ],

      trueFalseOptions: [
        { text: 'True', value: 'true' },
        { text: 'False', value: 'false' }
      ]
    }
  },

  watch: {
    question: {
      handler(newVal) {
        this.resetForm()
        if (newVal) {
          this.loadQuestionData(newVal)
        }
      },
      immediate: true
    },

    visible(newVal) {
      if (newVal) {
        this.resetForm()
        if (this.question) {
          this.loadQuestionData(this.question)
        }
      }
    },

    'form.question_type'(newVal) {
      // Reset correct answer when question type changes
      if (newVal !== 'multiple_choice') {
        this.form.correct_answer_index = null
      }
    }
  },

  computed: {
    isTestPackValid() {
      return this.testPack && this.testPack.id
    }
  },

  methods: {
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null
    },

    resetForm() {
      this.form = {
        question_type: 'multiple_choice',
        question: '',
        explanation: '',
        points: 1,
        difficulty_level: 'medium',
        options: ['', '', '', ''],
        correct_answer: '',
        correct_answer_index: null,
        essay_prompt: '',
        min_words: null,
        max_words: null
      }

      this.$nextTick(() => {
        if (this.$refs.formObserver) {
          this.$refs.formObserver.reset()
        }
      })
    },

    loadQuestionData(question) {
      const options = question.options || ['', '', '', '']
      const correctAnswer = question.correct_answer || ''
      const correctAnswerIndex = options.indexOf(correctAnswer)

      this.form = {
        question_type: question.question_type || 'multiple_choice',
        question: question.question || '',
        explanation: question.explanation || '',
        points: question.points || 1,
        difficulty_level: question.difficulty_level || 'medium',
        options: options,
        correct_answer: correctAnswer,
        correct_answer_index: correctAnswerIndex >= 0 ? correctAnswerIndex : null,
        essay_prompt: question.essay_prompt || '',
        min_words: question.min_words,
        max_words: question.max_words
      }
    },

    addOption() {
      if (this.form.options.length < 6) {
        this.form.options.push('')
      }
    },

    removeOption(index) {
      if (this.form.options.length > 2) {
        this.form.options.splice(index, 1)

        // Clear correct answer if it was the removed option
        if (this.form.correct_answer_index === index) {
          this.form.correct_answer_index = null
          this.form.correct_answer = ''
        } else if (this.form.correct_answer_index > index) {
          // Adjust index if an option before the correct answer was removed
          this.form.correct_answer_index -= 1
        }
      }
    },

    setCorrectAnswer(index) {
      this.form.correct_answer_index = index
      this.form.correct_answer = this.form.options[index]
    },

    handleSubmit() {
      this.$refs.formObserver.validate().then(success => {
        if (success) {
          this.submitForm()
        }
      })
    },

    async submitForm() {
      this.isLoading = true

      try {
        if (!this.isTestPackValid) {
          this.$message.error('Test pack is required. Please select a test pack first.')
          return
        }

        // For multiple choice, set correct_answer from the selected index
        if (this.form.question_type === 'multiple_choice' && this.form.correct_answer_index !== null) {
          this.form.correct_answer = this.form.options[this.form.correct_answer_index]
        }

        const formData = {
          ...this.form,
          test_pack_id: this.testPack.id,
          options: this.form.question_type === 'multiple_choice' ? JSON.stringify(this.form.options.filter(opt => opt.trim())) : null
        }

        const api = new TestManagementApi()
        let result

        if (this.question) {
          result = await api.updateQuestion(this.question.id, formData)
        } else {
          result = await api.createQuestion(formData)
        }

        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            text: this.question
              ? 'Question updated successfully'
              : 'Question created successfully',
            variant: 'success',
          },
        })

        this.$emit('save', result.data)

      } catch (error) {
        console.error('Error saving question:', error)
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: error.response?.data?.error || 'Failed to save question',
            variant: 'danger',
          },
        })
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.question-modal {
  .question-type-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;

    .type-card {
      padding: 1rem;
      border: 2px solid #e9ecef;
      border-radius: 0.5rem;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        border-color: #7367F0;
        background: #f8f9fa;
      }

      &.active {
        border-color: #7367F0;
        background: linear-gradient(118deg, #7367F0, rgba(115, 103, 240, 0.1));
        color: white;

        h6, small {
          color: white;
        }
      }

      h6 {
        color: #2c2c2c;
        font-weight: 600;
      }

      small {
        color: #6c757d;
      }
    }
  }

  .option-row {
    .input-group-text {
      font-weight: 600;
    }
  }

  h5 {
    color: #2c2c2c;
    font-weight: 600;
    display: flex;
    align-items: center;

    svg {
      color: #7367F0;
    }
  }
}

::v-deep .modal-header {
  background: linear-gradient(118deg, #7367F0, rgba(115, 103, 240, 0.7));
  color: white;
  border-bottom: none;

  .modal-title {
    color: white !important;
  }

  .close {
    color: white;
    opacity: 0.8;

    &:hover {
      opacity: 1;
    }
  }
}
</style>